import React, { useState, useMemo, useEffect, useActionState } from 'react'
import {
  IconFiles,
  IconLayoutGrid,
  IconLayoutList,
  IconX,
  IconSearch,
  IconChevronDown,
  IconChevronRight,
  IconChevronLeft,
  IconTrash,
  IconPackage,
  IconDotsVertical,
  IconEdit,
  IconDatabase,
  IconUpload,
  IconFileText,
  IconFile,
  IconLayoutColumns,
  IconSquarePlus,
  IconLink,
  IconChartBar
} from '@tabler/icons-react'
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Spinner
} from 'reactstrap'
//@ts-ignore
import { Card, CardBody, Badge, Button, InputGroup, Checkbox } from 'dsm-react-lib'
import { DataProductsIcon, FilesIcon } from '../icons/Icons'
import { StyledDataTable } from '../../Applications/styles'
import DataboxItems, { MixedCardItem } from '../../Teams/DataboxItems'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
// import { DataboxItemData, databoxItems } from '../../Teams/SampleTableData'
// import { databoxData, DataboxData } from '../../Teams/databoxData'
import { Databox } from '../../Teams/modals/AddDataboxModal'
import { useAgentContext } from './AgentFormContext'
import { useAgentService } from './agentService'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { buildAgentMcpServersArray } from './dataSourceUtils'
import { getFileIcon, convertCustomSelectionToODataFilter } from '../utils/helpers'
import EmptyContent from '../../Teams/EmptyContent'
import { MCP_SERVER_NAMES } from '../utils/constants'
import {
  buildPersonalDataBoxesPayload,
  findMcpScopeConfiguration,
  haveComparableMcpScopesChanged
} from '../utils/mcpServerUtils'

interface DataSourcesListProps {
  setStep: (step: 'details' | 'tools' | 'dataSources') => void
  avatarFile: File | null
}

const DataSourcesList: React.FC<DataSourcesListProps> = ({ setStep, avatarFile }) => {
  const [selectedRows, setSelectedRows] = useState<string[]>([])
  const { id } = useParams<{ id: string }>()
  const { teamId } = useParams<{ teamId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchValue, setSearchValue] = useState('')
  const [selectAll, setSelectAll] = useState(false)
  const [openFilterSidebar, setOpenFilterSidebar] = useState(false)
  const [selectedTab, setSelectedTab] = useState('All')
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list')
  const [expandedDataboxes, setExpandedDataboxes] = useState<Set<string>>(new Set())
  const [allItemsWereRemoved, setAllItemsWereRemoved] = useState(false)
  const { createAgent, updateAgentDetails, getAllMCPServers } = useAgentService()
  const {
    mixedAssetData,
    removeFromMixedAssetData: originalRemoveFromMixedAssetData,
    originalDataSourcesByMcp,
    isDataSourcesInitialized,
    setIsAddDataboxModalOpen,
    setEditingDataboxId,
    setIsAddDataProductModalOpen,
    isWebMCPEnabled
  } = useAgentContext()

  // Wrapper for removeFromMixedAssetData that sets the flag when everything is removed
  const removeFromMixedAssetData = (ids: string[]) => {
    const remainingItems = mixedAssetData.filter((item) => !ids.includes(item.id))

    // If we had items before and now we'll have none, set the flag
    if (mixedAssetData.length > 0 && remainingItems.length === 0) {
      setAllItemsWereRemoved(true)
    }

    originalRemoveFromMixedAssetData(ids)
  }
  const { agent } = useSelector((state: any) => state.agentReducer)
  const { mcpServers } = useSelector((state: any) => state.chatbotReducer)

  // Human-readable source label for the "Source" column. A databox mixes files and data products, so its label depends on
  // what's actually selected inside it.
  const getSourceLabel = (row: any): string => {
    if (row.type === 'file') return 'SmartDocs'
    if (row.type === 'dataproduct') return 'Data Marketplace'
    if (row.type === 'databox') {
      const items = row.selectedItems
      if (!items || items.length === 0) return 'SmartDocs' // whole databox selected
      const hasFiles = items.some((item: any) => item.type === 'file')
      const hasDataProducts = items.some((item: any) => item.type === 'dataproduct')
      if (hasFiles && hasDataProducts) return 'Mixed sources'
      return hasDataProducts ? 'Data Marketplace' : 'SmartDocs'
    }
    return row.source ?? '-'
  }

  // Compute filtered data based on search and tab selection
  const searchFilteredData = useMemo(() => {
    // First filter by tab
    let tabFilteredData = mixedAssetData || []
    if (selectedTab === 'Files') {
      tabFilteredData = mixedAssetData.filter((item) => item.type === 'file')
    } else if (selectedTab === 'Data Products') {
      tabFilteredData = mixedAssetData.filter((item) => item.type === 'dataproduct')
    } else if (selectedTab === 'Databoxes') {
      tabFilteredData = mixedAssetData.filter((item) => item.type === 'databox')
    }

    // Then apply search filter if search value exists
    let filtered = tabFilteredData
    if (searchValue.length) {
      filtered = tabFilteredData?.filter((item) => {
        const startsWith =
          item.name.toLowerCase().startsWith(searchValue.toLowerCase()) ||
          item?.source?.toLowerCase().startsWith(searchValue.toLowerCase()) ||
          item?.owner?.toLowerCase().startsWith(searchValue.toLowerCase()) ||
          item?.tags?.some((tag: string) => tag.toLowerCase().startsWith(searchValue.toLowerCase()))
        const includes =
          item.name.toLowerCase().includes(searchValue.toLowerCase()) ||
          item?.source?.toLowerCase().includes(searchValue.toLowerCase()) ||
          item?.owner?.toLowerCase().includes(searchValue.toLowerCase()) ||
          item?.tags?.some((tag: string) => tag.toLowerCase().includes(searchValue.toLowerCase()))
        return startsWith || includes
      })
    }

    // `id` values come from independent backend sequences per type (a personal databox, a team
    // databox and a data product can all legitimately share the same numeric id) - the table
    // library keys rows by a single field, so without a type-qualified key two different rows
    // with the same id collide and only one of them actually renders. `_rowKey` disambiguates
    // this without touching `id` itself (still used everywhere else for selection/removal).
    // Always show My Personal Databox first, then Team Databoxes, then Data Products/files -
    // regardless of the order things were selected in (stable sort keeps ties in place).
    const sourceOrder = (item: any) => {
      if (item.type === 'databox') return item.scope === 'Personal' ? 0 : 1
      return 2
    }
    return (filtered || [])
      .slice()
      .sort((a, b) => sourceOrder(a) - sourceOrder(b))
      .map((item: any) => ({
        ...item,
        _rowKey: `${item.type}-${item.smartDocsDataboxId ?? item.id}`
      }))
  }, [mixedAssetData, selectedTab, searchValue])
  // Get databox data from navigation state or fallback to first databox
  // const currentDatabox: DataboxData | undefined =
  //   location.state?.databoxData ||
  //   databoxData.find((d) => d.id === parseInt(id || '1')) ||
  //   databoxData[0]

  // Calculate counts from actual databoxItems
  // const filesCount = databoxItems.filter((item) => item.type === 'file').length

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRows([])
      setSelectAll(false)
    } else {
      setSelectedRows(searchFilteredData.map((row) => row?.id))
      setSelectAll(true)
    }
  }

  const handleRowSelect = (id: string) => {
    if (selectedRows.includes(id)) {
      setSelectedRows(selectedRows.filter((rowId: any) => rowId !== id))
    } else {
      setSelectedRows([...selectedRows, id])
    }
  }

  const columns: any[] = [
    {
      name: (
        <Checkbox
          type="checkbox"
          name="select-all"
          id="select-all"
          checked={selectAll}
          onChange={() => handleSelectAll()}
          indeterminate={selectedRows.length > 0 && selectedRows.length < searchFilteredData.length}
        />
      ),
      cell: (row: any) => (
        <Checkbox
          type="checkbox"
          name={`select-row-${row.id}`}
          id={`select-row-${row.id}`}
          checked={selectedRows.includes(row.id)}
          onChange={() => handleRowSelect(row.id)}
        />
      ),
      sortable: false,
      width: '30px'
    },
    {
      name: 'Name',
      selector: (row: any, rowIndex?: number) => row.name,
      sortable: true,
      width: '500px',
      cell: (row: any) => {
        const hasSelectedItems =
          row.type === 'databox' && Array.isArray(row.selectedItems) && row.selectedItems.length > 0

        const isExpanded = expandedDataboxes.has(row.id)

        return (
          <div className="w-full">
            <div className="flex items-center">
              {/* Chevron button for databoxes with selectedItems */}
              {hasSelectedItems && (
                <div
                  onClick={(e) => {
                    e.stopPropagation()
                    setExpandedDataboxes((prev) => {
                      const next = new Set(prev)
                      if (next.has(row.id)) {
                        next.delete(row.id)
                      } else {
                        next.add(row.id)
                      }
                      return next
                    })
                  }}
                  className="bg-transparent border-0 cursor-pointer mr-2 p-0 flex items-center flex-shrink-0 hover:opacity-70 transition-opacity">
                  {isExpanded ? (
                    <IconChevronDown
                      size={16}
                      className="transition-all duration-300 ease-in-out"
                    />
                  ) : (
                    <IconChevronRight
                      size={16}
                      className="transition-all duration-300 ease-in-out"
                    />
                  )}
                </div>
              )}
              {row.type === 'file' ? (
                <IconFiles size={20} strokeWidth={2} className="text-primary" />
              ) : row.type === 'dataproduct' ? (
                <IconDatabase size={20} className="text-success" />
              ) : (
                <IconPackage size={20} className="text-muted" />
              )}
              <div className="font-semibold text-[15px] leading-tight truncate text-gray-900 ml-2">
                {row.name}
              </div>
            </div>

            {/* Expandable file list */}
            <div
              className={`transition-all duration-500 ease-in-out ${
                hasSelectedItems && isExpanded
                  ? 'h-auto opacity-100 mt-[8px] overflow-visible'
                  : 'max-h-0 opacity-0 mt-0 overflow-hidden'
              }`}>
              <div className="ml-1 space-y-1 pr-2">
                {row.selectedItems?.map((item: any, idx: number) => {
                  return (
                    <div
                      key={idx}
                      className={`p-[8px] rounded transition-colors ${
                        idx < row.selectedItems.length - 1 ? 'border-b border-gray-100' : ''
                      }`}>
                      {item.type === 'file' ? (
                        <div className="flex items-start gap-1.5 ml-[1rem]">
                          {getFileIcon(item.name)}
                          <span className="text-sm leading-relaxed text-gray-700 break-words">
                            {item.name}
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-start gap-1.5 ml-[10px]">
                          <IconDatabase size={20} className="flex-shrink-0 mt-0.5 ml-0" />
                          <span className="text-sm leading-relaxed text-gray-700 break-words">
                            {item.name}
                          </span>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        )
      }
    },
    {
      name: '',
      allowOverflow: true,
      width: '30px',
      cell: (row: any) => (
        <div className="d-flex relative z-1000">
          <UncontrolledDropdown>
            <DropdownToggle tag="span" className="cursor-pointer">
              <IconDotsVertical size={15} className="text-theme-subheading" />
            </DropdownToggle>
            <DropdownMenu className="z-1050 theme-surface border theme-border-default shadow-sm">
              {/* <DropdownItem className="w-100 text-primary">
                <div className="d-flex align-items-center">
                  <IconChartBar size={15} />
                  <span className="align-middle ms-2">View Details</span>
                </div>
              </DropdownItem>
              <DropdownItem className="w-100 text-primary">
                <div className="d-flex align-items-center">
                  <Download size={15} />
                  <span className="align-middle ms-2">Download</span>
                </div>
              </DropdownItem> */}
              {row.type === 'databox' && (
                <DropdownItem
                  className="w-100 theme-brand-accent-bold"
                  onClick={() => {
                    setEditingDataboxId(row.id)
                    setIsAddDataboxModalOpen(true)
                  }}>
                  <div className="d-flex align-items-center">
                    <IconEdit size={15} />
                    <span className="align-middle ms-2">Edit</span>
                  </div>
                </DropdownItem>
              )}
              <DropdownItem
                className="w-100 text-danger"
                onClick={() => removeFromMixedAssetData([row.id])}>
                <div className="d-flex align-items-center">
                  <IconTrash size={15} />
                  <span className="align-middle ms-2">Delete</span>
                </div>
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>
      )
    },
    {
      name: 'Files',
      selector: (row: any) => row.filesCount || 0,
      sortable: true,
      width: '220px',
      cell: (row: any) => {
        if (row.type !== 'databox') return <div className="text-gray-500">-</div>

        const selectedCount = row.selectedItems?.length || 0
        const totalCount = row.filesCount || 0
        // The personal databox doesn't carry a meaningful total file count to compare against
        // (see the "X files selected" label below), so treat any selection there as "full" for
        // the circle indicator instead of showing it as empty/partial.
        const percentage =
          row.scope === 'Personal'
            ? selectedCount > 0
              ? 100
              : 0
            : totalCount > 0
            ? (selectedCount / totalCount) * 100
            : 0

        // Show selection format if custom files selected
        if (row.selectedItems) {
          // Render circle based on percentage
          const renderCircle = () => {
            if (percentage === 0) {
              // Empty circle with border
              return (
                <svg width="20" height="20" viewBox="0 0 20 20">
                  <circle
                    cx="10"
                    cy="10"
                    r="8"
                    fill="var(--surface-elevated)"
                    stroke="var(--color-primary)"
                    strokeWidth="1"
                  />
                </svg>
              )
            }

            if (percentage === 100) {
              // Fully filled circle
              return (
                <svg width="20" height="20" viewBox="0 0 20 20">
                  <circle cx="10" cy="10" r="8" fill="var(--color-primary)" stroke="none" />
                </svg>
              )
            }

            // Partial fill - pie chart
            const angle = (percentage / 100) * 360
            const largeArcFlag = angle > 180 ? 1 : 0
            const endX = 10 + 8 * Math.sin((angle * Math.PI) / 180) // eslint-disable-line no-mixed-operators
            const endY = 10 - 8 * Math.cos((angle * Math.PI) / 180) // eslint-disable-line no-mixed-operators

            return (
              <svg width="20" height="20" viewBox="0 0 20 20">
                <circle
                  cx="10"
                  cy="10"
                  r="8"
                  fill="var(--surface-elevated)"
                  stroke="var(--color-primary)"
                  strokeWidth="1"
                />
                <path
                  d={`M 10 10 L 10 2 A 8 8 0 ${largeArcFlag} 1 ${endX} ${endY} Z`}
                  fill="var(--color-primary)"
                />
              </svg>
            )
          }

          return (
            <div className="flex items-start gap-1">
              <div className="position-relative d-inline-flex">{renderCircle()}</div>
              <div>
                <div className="font-medium text-gray-900">
                  {row.scope === 'Personal'
                    ? `${selectedCount} file${selectedCount === 1 ? '' : 's'} selected`
                    : `${selectedCount} of ${totalCount} selected`}
                </div>
                <div className="text-sm text-gray-500">
                  Files size: {row.totalSizeFormatted ?? '0 B'}
                </div>
              </div>
            </div>
          )
        }

        // Show total count if whole databox selected (fully filled circle)
        return (
          <div className="flex items-start gap-1">
            <div className="position-relative d-inline-flex">
              <svg width="20" height="20" viewBox="0 0 20 20">
                <circle cx="10" cy="10" r="8" fill="var(--color-primary)" stroke="none" />
              </svg>
            </div>
            <div>
              <div className="font-medium text-gray-900">{totalCount}</div>
              <div className="text-sm text-gray-500">
                Files size: {row.totalSizeFormatted ?? '0 B'}
              </div>
            </div>
          </div>
        )
      }
    },
    {
      name: 'Data Products',
      selector: (row: any) => row.dataProductsCount || 0,
      sortable: true,
      width: '250px',
      cell: (row: any) => {
        if (row.type !== 'databox') return <div className="text-gray-500">-</div>

        // Count selected data products from selectedItems array (if they have type 'dataproduct').
        const selectedCount =
          row.selectedItems?.filter((item: any) => item.type === 'dataproduct')?.length || 0
        const totalCount = row.dataProductsCount || 0
        const percentage = totalCount > 0 ? (selectedCount / totalCount) * 100 : 0

        // Show selection format if custom items selected or if there are data products
        if (row.selectedItems) {
          // Render circle based on percentage
          const renderCircle = () => {
            if (percentage === 0) {
              // Empty circle with border
              return (
                <svg width="20" height="20" viewBox="0 0 20 20">
                  <circle
                    cx="10"
                    cy="10"
                    r="8"
                    fill="var(--surface-elevated)"
                    stroke="var(--color-primary)"
                    strokeWidth="1"
                  />
                </svg>
              )
            }

            if (percentage === 100) {
              // Fully filled circle
              return (
                <svg width="20" height="20" viewBox="0 0 20 20">
                  <circle cx="10" cy="10" r="8" fill="var(--color-primary)" stroke="none" />
                </svg>
              )
            }

            // Partial fill - pie chart
            const angle = (percentage / 100) * 360
            const largeArcFlag = angle > 180 ? 1 : 0
            const endX = 10 + 8 * Math.sin((angle * Math.PI) / 180) // eslint-disable-line no-mixed-operators
            const endY = 10 - 8 * Math.cos((angle * Math.PI) / 180) // eslint-disable-line no-mixed-operators

            return (
              <svg width="20" height="20" viewBox="0 0 20 20">
                <circle
                  cx="10"
                  cy="10"
                  r="8"
                  fill="var(--surface-elevated)"
                  stroke="var(--color-primary)"
                  strokeWidth="1"
                />
                <path
                  d={`M 10 10 L 10 2 A 8 8 0 ${largeArcFlag} 1 ${endX} ${endY} Z`}
                  fill="var(--color-primary)"
                  className="transition-all duration-300 ease-in-out"
                />
              </svg>
            )
          }

          return (
            <div className="flex items-start gap-1">
              <div className="position-relative d-inline-flex">{renderCircle()}</div>
              <div>
                <div className="font-medium text-gray-900">
                  {selectedCount} of {totalCount} selected
                </div>
                <div className="text-sm text-gray-500">Source: Data marketplace</div>
              </div>
            </div>
          )
        }

        // Whole databox selected - show fully filled circle if there are data products
        if (totalCount > 0) {
          return (
            <div className="flex items-start gap-1">
              <div className="position-relative d-inline-flex">
                <svg width="20" height="20" viewBox="0 0 20 20">
                  <circle cx="10" cy="10" r="8" fill="var(--color-primary)" stroke="none" />
                </svg>
              </div>
              <div>
                <div className="font-medium text-gray-900">{totalCount} selected</div>
              </div>
            </div>
          )
        }

        // No data products at all
        return <div className="text-gray-500">-</div>
      }
    },

    {
      name: 'Source',
      selector: (row: any, rowIndex?: number) => getSourceLabel(row),
      sortable: true,
      width: '150px',
      cell: (row: any) => <div className="text-sm text-theme-subheading">{getSourceLabel(row)}</div>
    },
    {
      name: 'Owner/Uploaded By',
      selector: (row: any, rowIndex?: number) => row.owner,
      sortable: true,
      width: '180px',
      cell: (row: any) => (
        <div className="d-flex align-items-center">
          <span className="text-sm text-theme-subheading">{row.owner}</span>
        </div>
      )
    },
    {
      name: 'Date Added',
      selector: (row: any, rowIndex?: number) => row.dateAdded,
      sortable: true,
      width: '130px',
      cell: (row: any) => (
        <div className="text-sm text-theme-subheading">
          {row.dateAdded ? new Date(row.dateAdded).toLocaleDateString() : '-'}
        </div>
      )
    },

    {
      name: 'Tags',
      selector: (row: any, rowIndex?: number) => row?.tags?.join(', '),
      sortable: false,
      width: '270px',
      cell: (row: any) => (
        <div className="d-flex flex-wrap">
          {row?.tags?.slice(0, 1).map((tag: any, index: number) => (
            <Badge
              key={index}
              label={tag}
              color="primary"
              className="theme-badge-tag"
              variant="light"
              type="pill"
            />
          ))}
          {row?.tags?.length > 1 && (
            <Badge
              label={`+${row?.tags?.length - 1}`}
              color="primary"
              variant="light"
              type="pill"
            />
          )}
        </div>
      )
    }
  ]

  // Simplified filter function - just sets search value, useMemo handles the rest
  const handleFilter = (value: string) => {
    setSearchValue(value)
  }

  // Reset component state when agent changes
  useEffect(() => {
    // Clear all local state to prevent stale data from previous agent
    setSelectedRows([])
    setSearchValue('')
    setSelectAll(false)
    setSelectedTab('All')
    setExpandedDataboxes(new Set())
    setAllItemsWereRemoved(false)
  }, [agent?.id])

  // Watch for items being added back after removal
  useEffect(() => {
    if (mixedAssetData.length > 0 && allItemsWereRemoved) {
      setAllItemsWereRemoved(false)
    }
  }, [mixedAssetData.length, allItemsWereRemoved])

  function handleEditDatabox(): void {
    // Navigate to the edit databox page for the current team
    navigate(`/admin-teams/${id || '1'}/edit-databox`)
  }

  // Calculate row height based on density
  const getRowHeight = () => {
    switch (tableDensity) {
      case 'compact':
        return { height: 30, className: 'table-compact' }
      case 'spacious':
        return { height: 60, className: 'table-spacious' }
      default: // comfortable
        return { height: 48, className: 'table-comfortable' }
    }
  }
  const [tableDensity, setTableDensity] = useState<'comfortable' | 'compact' | 'spacious'>(
    'comfortable'
  )

  const [, handleSubmitAgent, isSubmitting] = useActionState(async () => {
    const agentBasicData = {
      ...agent,
      isPersonalAssistant: agent && agent?.isPersonalAssistant
    }

    // Build OData filter query from databoxes relevant to SmartInsights only. A databox counts
    // toward SmartInsights scope when it's fully selected (no partial selectedItems) or has at
    // least one FILE selected. The Personal Databox is excluded - it's saved separately via its
    // own dedicated PersonalDatabox scope entry below.
    const allDataboxes = mixedAssetData.filter((item: any) => {
      if (item.type !== 'databox' || item.scope === 'Personal') return false
      const items = item.selectedItems
      if (!items || items.length === 0) return true // whole databox selected
      return items.some((selectedItem: any) => selectedItem.type === 'file')
    })

    let odataFilter = ''
    if (allDataboxes.length > 0) {
      // Build documentsMap: documentId -> cognitiveSearchId (only for databoxes with selected documents)
      const documentsMap: Record<string | number, string> = {}

      allDataboxes.forEach((databox: any) => {
        // Map documentIds from selectedItems to cognitiveSearchId
        databox.selectedItems?.forEach((item: any) => {
          if (item.smartDocsDocumentId && item.cognitiveSearchId) {
            documentsMap[item.smartDocsDocumentId] = item.cognitiveSearchId
          }
        })
      })

      // Build unified customSelection object for OData conversion (documents only)
      const unifiedCustomSelection = {
        databox: allDataboxes.map((databox: any) => {
          const databoxEntry: any = {
            id: databox.smartDocsDataboxId || databox.id
          }

          // Extract document IDs (files) from selectedItems for OData filter
          if (databox.selectedItems && databox.selectedItems.length > 0) {
            const documentIds = databox.selectedItems
              .filter((item: any) => item.type === 'file' && item.smartDocsDocumentId)
              .map((item: any) => item.smartDocsDocumentId)

            if (documentIds.length > 0) {
              databoxEntry.documentIds = documentIds
            }
          }

          return databoxEntry
        })
      }

      // Generate OData filter query
      odataFilter = convertCustomSelectionToODataFilter(unifiedCustomSelection, documentsMap)
    }

    // Find MCP servers
    const SnowflakeMCP = mcpServers?.find(
      (server: any) => server.name === MCP_SERVER_NAMES.SNOWFLAKE
    )
    const SmartInsightsMCP = mcpServers?.find(
      (server: any) => server.name === MCP_SERVER_NAMES.SMART_INSIGHTS
    )
    const WebSearchMCP = isWebMCPEnabled
      ? mcpServers?.find((server: any) => server.name === MCP_SERVER_NAMES.WEB_SEARCH)
      : null
    const CodeInterpreterMCP = mcpServers?.find(
      (server: any) => server.name === MCP_SERVER_NAMES.CODE_INTERPRETER
    )

    const smartInsightsMcpServerId = SmartInsightsMCP?.id || null // SmartInsights
    const snowflakeMcpServerId = SnowflakeMCP?.id || null // Snowflake
    const webSearchMcpServerId = WebSearchMCP?.id || null // WebSearch
    const codeInterpreterMcpServerId = CodeInterpreterMCP?.id || null // CodeInterpreter

    // Build array of MCP server configs with proper routing
    // Databoxes → both servers, Data products → Snowflake only. The Personal Databox is excluded
    // here (added separately below via its own dedicated PersonalDatabox scope entry) so it never
    // leaks into the team-oriented teamIdentifier/fullySelectedDataboxIds fields.
    const teamScopedMixedAssetData = mixedAssetData.filter(
      (item: any) => !(item.type === 'databox' && item.scope === 'Personal')
    )
    const agentMcpServers = buildAgentMcpServersArray(
      teamScopedMixedAssetData,
      smartInsightsMcpServerId,
      snowflakeMcpServerId,
      webSearchMcpServerId,
      codeInterpreterMcpServerId,
      originalDataSourcesByMcp,
      agent?.id
    )

    // Add filter to SmartInsights MCP server if OData filter was generated
    if (odataFilter && smartInsightsMcpServerId) {
      const smartInsightsServer = agentMcpServers.find(
        (server) => server.mcpServerId === smartInsightsMcpServerId
      )

      if (smartInsightsServer) {
        // A databox was added/removed, so this entry is already in the payload - just add the filter
        ;(smartInsightsServer as any).filter = odataFilter
      } else {
        // No databox was added/removed, but files selected inside a databox may have changed.
        // Compare the new filter to the saved one to check for that case.
        const originalFilter =
          findMcpScopeConfiguration(agent?.agentMcpServers, smartInsightsMcpServerId)?.filters || ''

        // Filter changed, so create a new entry for it
        if (odataFilter !== originalFilter) {
          const databoxes = teamScopedMixedAssetData.filter((item: any) => item.type === 'databox')
          const teamIdentifier = databoxes.find((item) => item.teamIdentifier)?.teamIdentifier

          const newServer = {
            mcpServerId: smartInsightsMcpServerId,
            ...(agent?.id && { agentId: agent.id }),
            ...(teamIdentifier && { teamIdentifier }),
            filter: odataFilter
          }
          agentMcpServers.push(newServer)
        }
      }
    }

    // Personal Databox: separate top-level payload field (personalDataBoxes)
    let personalDataBoxes = buildPersonalDataBoxesPayload(mixedAssetData as any)
    const hadOriginalPersonalDatabox = !!agent?.personalDataBoxes?.length
    if (!personalDataBoxes && hadOriginalPersonalDatabox) {
      // Selection was cleared entirely - send an empty array to clear it on the backend
      personalDataBoxes = []
    }

    // Drop servers that carry no actual scope except for the web search MCP (would serialize to an empty scopeConfiguration)
    const nonEmptyMcpServers = agentMcpServers.filter(
      (s: any) =>
        s.filter ||
        s.dataProductScopes?.length ||
        s.dataProductGroups?.length ||
        s.fullySelectedDataboxIds?.length ||
        s.mcpServerId === webSearchMcpServerId ||
        s.mcpServerId === codeInterpreterMcpServerId
    )

    // Determine agentMcpServers value based on the flag
    let finalAgentMcpServers
    if (!isWebMCPEnabled && allItemsWereRemoved && mixedAssetData.length === 0) {
      // Items were explicitly removed, send empty array to clear
      finalAgentMcpServers = []
    } else {
      finalAgentMcpServers = haveComparableMcpScopesChanged(
        nonEmptyMcpServers,
        agent?.agentMcpServers || []
      )
        ? nonEmptyMcpServers
        : undefined
    }

    const agentData = {
      ...agentBasicData,
      agentMcpServers: finalAgentMcpServers,
      personalDataBoxes,
      avatarFile,
      otherMCPs: [webSearchMcpServerId, codeInterpreterMcpServerId].filter(Boolean)
    }

    const response = agent?.id
      ? await updateAgentDetails(agent.id, agentData)
      : await createAgent(agentData)
    if (response?.agent) {
      if (response?.agent) {
        toast.success('Agent saved successfully')
      } else {
        toast.error('Error saving agent')
      }
      // Both destinations load their own agent list on mount
      navigate(teamId ? `/teams/${teamId}` : '/manage-agents')
    } else {
      toast.error(response?.message)
    }
    return null
  }, null)

  // Calculate tab counts from mixedAssetData
  const databoxCount = useMemo(
    () => mixedAssetData.filter((item) => item.type === 'databox').length,
    [mixedAssetData]
  )
  const dataProductCount = useMemo(
    () => mixedAssetData.filter((item) => item.type === 'dataproduct').length,
    [mixedAssetData]
  )

  return (
    <div className="p-0 bg-transparent">
      {/* Tabs */}
      {/* Main Data Table Card */}
      <Card className="pt-0 pb-1 theme-surface border theme-border-default">
        <CardBody className="p-0 theme-surface">
          {/* Search and Actions Bar */}
          <div className="mb-1">
            <div className="d-flex align-items-center mb-0 gap-50">
              <div className="d-flex align-items-center mb-1">
                {[
                  { label: 'All', icon: null },
                  {
                    label: 'Databoxes',
                    icon:
                      selectedTab === 'Databoxes' ? (
                        <IconPackage size={20} />
                      ) : (
                        <IconPackage size={25} />
                      )
                  },
                  // {
                  //   label: 'Files',
                  //   icon: selectedTab === 'Files' ? <FilesIcon size={25} /> : <FilesIcon size={25} />
                  // },
                  {
                    label: 'Data Products',
                    icon:
                      selectedTab === 'Data Products' ? (
                        <IconDatabase size={20} />
                      ) : (
                        <DataProductsIcon size={25} />
                      )
                  }
                ].map((tab) => {
                  const isSelected = selectedTab === tab.label

                  return (
                    <button
                      key={tab.label}
                      type="button"
                      className={`data-tab-view btn bg-transparent p-1 position-relative d-flex align-items-center text-[16px] border-none rounded-none ${
                        isSelected
                          ? 'text-primary tab-selected'
                          : 'text-muted border-b-2 border-transparent'
                      }`}
                      onClick={() => setSelectedTab(tab.label)}>
                      {tab.icon ? <span className="data-tab-icon">{tab.icon}</span> : null}
                      &nbsp;&nbsp;
                      {tab.label}
                      {tab.label === 'Databoxes' && (
                        <span className="ms-50 text-theme-subheading">{databoxCount}</span>
                      )}
                      {tab.label === 'Data Products' && (
                        <span className="ms-50 text-theme-subheading">{dataProductCount}</span>
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
            {/* Show search bar when there's data OR when search is active */}
            {(mixedAssetData?.length > 0 || searchValue.length > 0) && (
              <div className="flex align-items-center">
                {/* Search Input */}
                <div className="flex w-75 gap-1">
                  <InputGroup
                    addon={<IconSearch size={14} />}
                    addonPosition="start"
                    addonType="merged"
                    placeholder="Search by files, products, owners, tags..."
                    value={searchValue}
                    onChange={(e: any) => handleFilter(e.target.value)}
                  />
                  {searchValue.length > 0 && (
                    <Button
                      className="whitespace-nowrap"
                      label={`Clear search`}
                      icon={<IconX size={15} />}
                      color="neutral"
                      variant="outline"
                      size="small"
                      onClick={() => handleFilter('')}
                    />
                  )}
                  {selectedRows.length > 0 && (
                    <Button
                      label={`Delete (${selectedRows.length})`}
                      icon={<IconTrash size={15} />}
                      color="danger"
                      size="small"
                      className="whitespace-nowrap"
                      onClick={() => {
                        removeFromMixedAssetData(selectedRows)
                        setSelectedRows([])
                        setSelectAll(false)
                      }}
                    />
                  )}
                </div>

                {/* Filter Button */}
                {/* <Button
                  label="Filters"
                  icon={<Filter size={18} />}
                  color="neutral"
                  variant="fill"
                  onClick={() => setOpenFilterSidebar(true)}
                /> */}
                {/* Manage Columns Button */}
                {/* <Button
                  label="Manage Columns"
                  variant="outline"
                  icon={<IconLayoutColumns size={15} />}
                  color="neutral"
                  className="ms-auto"
                /> */}
                {/* {searchFilteredData?.length > 0 && (
                  <div className={`flex items-center gap-1 chat-primary`}>
                    <div
                      onClick={() => setViewMode('list')}
                      title="List View"
                      className={`h-fit rounded !p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)] ${
                        viewMode === 'list' ? 'theme-selected-text' : 'theme-icon-muted'
                      }`}
                      aria-pressed={viewMode === 'list'}
                    >
                      <IconLayoutList size={24} />
                    </Button>
                    <Button
                      variant="flat"
                      onClick={() => setViewMode('grid')}
                      title="Grid View"
                      className={`h-fit rounded !p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)] ${
                        viewMode === 'grid' ? 'theme-selected-text' : 'theme-icon-muted'
                      }`}
                      aria-pressed={viewMode === 'grid'}
                    >
                      <IconLayoutGrid size={26} />
                    </Button>
                  </div>
                )} */}
              </div>
            )}
            {/* Show a loader while linked sources are still being fetched */}
            {!isDataSourcesInitialized && (
              <div className="mt-2">
                <div className="w-[400px] mx-auto p-6 text-center">
                  <Spinner className="mt-2 mb-3" color="primary" />
                  <h3 className="font-semibold text-theme-heading mb-2 text-xl">
                    Loading data sources...
                  </h3>
                  <p className="text-theme-subheading leading-relaxed">
                    Fetching this agent&apos;s linked databoxes and data products.
                  </p>
                </div>
              </div>
            )}
            {/* Show empty states when no data or no results */}
            {isDataSourcesInitialized && searchFilteredData?.length === 0 && (
              <div className="mt-2">
                {searchValue.length > 0 ? (
                  // Show "No results found" when search is active and returns no results
                  <div className="w-[400px] mx-auto border-2 border-dashed border-theme-border rounded-xl p-6 text-center bg-theme-surface shadow-sm">
                    <IconSearch
                      size={42}
                      className="mt-2 mb-3 text-theme-subheading"
                      strokeWidth={1.5}
                    />
                    <h3 className="font-semibold text-theme-heading mb-2 text-xl">
                      No results found
                    </h3>
                    <p className="text-theme-subheading leading-relaxed">
                      No items match your search criteria.
                      <br />
                      Try adjusting your search terms.
                    </p>
                  </div>
                ) : selectedTab === 'Databoxes' ? (
                  <div className="flex justify-center align-center py-2">
                    <EmptyContent
                      icon={<IconPackage size={40} stroke={1.5} color="var(--color-primary)" />}
                      title="No databoxes connected"
                      description="Connect existing personal or team databoxes to this Agent."
                      maxWidth="max-w-md"
                      btnLabel="Add Databoxes"
                      buttonOnClick={() => setIsAddDataboxModalOpen(true)}
                    />
                  </div>
                ) : selectedTab === 'Files' ? (
                  <div className="w-[400px] mx-auto border-2 border-dashed border-theme-brand-accent rounded-xl p-1 text-center bg-theme-surface shadow-sm">
                    <IconFile
                      size={42}
                      className="mt-3 mb-1 text-theme-brand-accent"
                      strokeWidth={1}
                    />
                    <h3 className="font-semibold text-theme-heading mb-1 text-2xl">Upload files</h3>
                    <p className="text-theme-subheading mb-1 leading-relaxed">
                      Drag and drop files, add a link
                      <br />
                      or browse from your computer.
                    </p>
                    <div className="flex gap-50 justify-center">
                      <Button
                        label="Add by link"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation()
                        }}
                        variant="outline"
                        color="gray"
                        icon={<IconLink size={16} />}
                      />
                      <Button
                        label="Browse files"
                        onClick={() => {
                          //  fileInputRef.current?.click()
                        }}
                        color="primary"
                        icon={<IconUpload size={16} />}
                      />
                    </div>
                  </div>
                ) : selectedTab === 'Data Products' ? (
                  <div className="flex justify-center align-center py-2">
                    <EmptyContent
                      icon={<IconDatabase size={40} stroke={1.5} color="var(--color-primary)" />}
                      title="No data products linked"
                      description="Search and link existing data products to this Agent."
                      maxWidth="max-w-md"
                      btnLabel="Search data products"
                      buttonOnClick={() => setIsAddDataProductModalOpen(true)}
                    />
                  </div>
                ) : (
                  <div className="flex flex-wrap justify-center gap-2 py-2">
                    <EmptyContent
                      icon={<IconPackage size={40} stroke={1.5} color="var(--color-primary)" />}
                      title="No databoxes connected"
                      description="Connect existing personal or team databoxes to this Agent."
                      maxWidth="max-w-md"
                      btnLabel="Add Databoxes"
                      buttonOnClick={() => setIsAddDataboxModalOpen(true)}
                    />
                    <EmptyContent
                      icon={<IconDatabase size={40} stroke={1.5} color="var(--color-primary)" />}
                      title="No data products linked"
                      description="Search and link existing data products to this Agent."
                      maxWidth="max-w-md"
                      btnLabel="Search data products"
                      buttonOnClick={() => setIsAddDataProductModalOpen(true)}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Data Table or Card View */}
          {isDataSourcesInitialized && searchFilteredData?.length > 0 && (
            <div className="overflow-auto max-h-[calc(100vh-350px)]">
              {viewMode === 'list' ? (
                <StyledDataTable
                  className={`react-dataTable list-table team-list-no-hover ${
                    getRowHeight().className
                  }`}
                  noHeader
                  keyField="_rowKey"
                  data={searchFilteredData}
                  columns={columns}
                  sortIcon={<IconChevronDown size={10} />}
                  key={`${selectAll ? 'all-selected' : 'not-all-selected'}-${tableDensity}`}
                  customStyles={{
                    headRow: {
                      style: {
                        minHeight: `${getRowHeight().height}px`,
                        backgroundColor: 'var(--surface-bg)',
                        color: 'var(--text-heading)',
                        borderBottomColor: 'var(--theme-border)',
                        borderBottomStyle: 'solid',
                        borderBottomWidth: '1px',
                        padding:
                          tableDensity === 'compact'
                            ? '2px 4px'
                            : tableDensity === 'spacious'
                            ? '8px 12px'
                            : '4px 0 !important'
                      }
                    },
                    rows: {
                      style: {
                        minHeight: `${getRowHeight().height}px`,
                        height: 'auto',
                        padding:
                          tableDensity === 'compact'
                            ? '2px 4px'
                            : tableDensity === 'spacious'
                            ? '8px 12px'
                            : '8px !important'
                      }
                    },
                    cells: {
                      style: {
                        height: 'auto !important',
                        minHeight: `${getRowHeight().height}px`,
                        alignItems: 'flex-start',
                        padding: '8px !important'
                      }
                    }
                  }}
                />
              ) : (
                <div>
                  {selectedTab === 'Files' ? (
                    <DataboxItems
                      mode="files"
                      items={searchFilteredData.filter(
                        (item): item is typeof item & { type: 'file' } => item.type === 'file'
                      )}
                    />
                  ) : selectedTab === 'Data Products' ? (
                    <DataboxItems
                      mode="dataProducts"
                      items={searchFilteredData.filter(
                        (item): item is typeof item & { type: 'dataproduct' } =>
                          item.type === 'dataproduct'
                      )}
                      onDelete={(id) => removeFromMixedAssetData([id])}
                    />
                  ) : (
                    <DataboxItems
                      mode="mixed"
                      items={searchFilteredData}
                      onDelete={(id) => removeFromMixedAssetData([id])}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </CardBody>
      </Card>
      <div className="flex justify-between items-center mt-2">
        <button
          type="button"
          onClick={() => navigate('/manage-agents')}
          className="flex items-center gap-50 rounded-md border theme-border-default bg-theme-surface-muted p-2.5 text-theme-heading transition-colors hover:bg-theme-hover hover:text-theme-brand-accent focus:outline-none focus:ring-1 focus:ring-theme-brand-accent">
          <IconChevronLeft size={16} />
          <span>Back to agents</span>
        </button>
        <div className="flex gap-1">
          <Button
            label="Previous step"
            className="theme-accent-outline-btn"
            onClick={() => setStep('tools')}
            variant="outline"
            color="primary"
            icon={<IconChevronLeft size={16} />}
          />
          <Button
            label={isSubmitting ? 'Submitting...' : 'Submit'}
            iconPosition="end"
            disabled={isSubmitting}
            icon={<IconChevronRight size={16} />}
            color="primary"
            onClick={() => handleSubmitAgent()}
          />
        </div>
      </div>
    </div>
  )
}

export default DataSourcesList
